/**
 * SbtVerifyBanner — Soft-nudge shown at top of GuestPortalPage when user hasn't
 * yet verified ID. Pure benefits messaging (no discount promises — getbitsy
 * doesn't control hotel pricing). Dismissible per session.
 */
import { useEffect, useState } from 'react';
import { ShieldCheck, X, ArrowRight } from 'lucide-react';
import { BACKEND_URL } from '@/lib/backendUrl';

const DISMISS_KEY = 'sbt_verify_banner_dismissed';

export default function SbtVerifyBanner({ email, onStartVerify }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (!email) return;
      if (sessionStorage.getItem(DISMISS_KEY)) return;
      try {
        const r = await fetch(`${BACKEND_URL}/api/id-verify/status/${encodeURIComponent(email)}`);
        const d = await r.json();
        if (!cancelled && !d.age_verified) setShow(true);
      } catch { /* silent */ }
    }
    check();
    return () => { cancelled = true; };
  }, [email]);

  if (!show) return null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setShow(false);
  };

  return (
    <div
      className="relative mb-4 rounded-xl border border-primary/30 bg-gradient-to-r from-primary/5 to-primary/10 p-4"
      data-testid="sbt-verify-banner"
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
        data-testid="sbt-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
          <ShieldCheck className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0 pr-6">
          <p className="text-sm font-semibold">Verify your ID once, book forever</p>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Get a Trusted Guest badge on your SBT. Skip ID upload at every future hotel.
            Faster front-desk check-in. We never store your DOB or ID photo.
          </p>
          <button
            onClick={onStartVerify || (() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }))}
            className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
            data-testid="sbt-banner-cta"
          >
            Verify now (2 min) <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
